/**
 * mini-galaxy.ts — tiny bottom-left strategic-map overlay (GALAXY §3.5.1).
 *
 * Shown only in system view. Gives the player strategic context while they're
 * zoomed into a tactical map: a 180×180px canvas with every system plotted
 * at its galaxy (x, y) and the focused system highlighted. Clicking another
 * system switches the tactical view's focusedSystemId.
 *
 * The mini-galaxy is a DOM overlay (not drawn on the main canvas) so it
 * doesn't interact with the main viewport rectangle / detail-panel / HUD
 * layout. The main `renderFrame` loop never touches it — it owns its own
 * canvas + paint cycle, driven from `update(state, ui)` on every frame.
 *
 * Pure reader during paint; click-handler mutates UiState directly (like
 * input.ts does) — never a Command, since focus is per-client UI state.
 */

import type { GameState, SystemId } from '../state/types';
import { defaultSystemCamera, type UiState } from './state';

export interface MiniGalaxyHandle {
  update(state: GameState, ui: UiState): void;
  destroy(): void;
}

/** Side length of the mini-galaxy canvas in CSS pixels (GALAXY §3.5.1). */
export const MINI_GALAXY_SIZE_PX = 180;

/** Pixel radius within which a click counts as "on" a system. */
const CLICK_THRESHOLD_PX = 10;

/** Star dot screen radius. Focused system gets a slightly larger ring. */
const DOT_RADIUS = 2.5;
const FOCUSED_RING_RADIUS = 6;

/**
 * Hit-test a click against the systems shown on the mini-galaxy. Pure
 * function split out so it can be unit-tested without mounting DOM.
 */
export function hitTestMiniGalaxy(
  systems: Iterable<{ id: SystemId; x: number; y: number }>,
  size: number,
  clickX: number,
  clickY: number,
  thresholdPx = CLICK_THRESHOLD_PX,
): SystemId | null {
  let best: { id: SystemId; distSq: number } | null = null;
  const thresholdSq = thresholdPx * thresholdPx;
  for (const sys of systems) {
    // Systems live in [0,1] galaxy coords; map directly into the square.
    const sx = sys.x * size;
    const sy = sys.y * size;
    const dx = sx - clickX;
    const dy = sy - clickY;
    const d2 = dx * dx + dy * dy;
    if (d2 <= thresholdSq && (!best || d2 < best.distSq)) {
      best = { id: sys.id, distSq: d2 };
    }
  }
  return best?.id ?? null;
}

export function mountMiniGalaxy(parent: HTMLElement): MiniGalaxyHandle {
  const root = document.createElement('div');
  root.id = 'mini-galaxy';
  root.title = 'Mini galaxy map — click a system to jump the camera there.';
  Object.assign(root.style, {
    position: 'fixed',
    left: '12px',
    bottom: '12px',
    width: `${MINI_GALAXY_SIZE_PX}px`,
    height: `${MINI_GALAXY_SIZE_PX}px`,
    background: 'rgba(6,10,20,0.82)',
    border: '1px solid rgba(80,110,160,0.35)',
    borderRadius: '4px',
    zIndex: '10',
    pointerEvents: 'auto',
    display: 'none',
    cursor: 'pointer',
  });

  const canvas = document.createElement('canvas');
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.floor(MINI_GALAXY_SIZE_PX * dpr);
  canvas.height = Math.floor(MINI_GALAXY_SIZE_PX * dpr);
  canvas.style.width = `${MINI_GALAXY_SIZE_PX}px`;
  canvas.style.height = `${MINI_GALAXY_SIZE_PX}px`;
  canvas.style.display = 'block';
  root.appendChild(canvas);

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Mini-galaxy: could not acquire 2D context');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  parent.appendChild(root);

  // Latest references kept fresh by update() so the click handler always
  // reads current state without needing closure gymnastics.
  let currentState: GameState | null = null;
  let currentUi: UiState | null = null;

  function onClick(e: MouseEvent): void {
    if (!currentState || !currentUi) return;
    if (currentUi.viewMode !== 'system') return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const hit = hitTestMiniGalaxy(
      currentState.systems.values(),
      MINI_GALAXY_SIZE_PX,
      x,
      y,
    );
    if (hit === null) return;
    if (hit === currentUi.focusedSystemId) return;
    // Snap to the clicked system. The cross-fade on intra-tactical focus
    // changes will come in a later milestone; for now a hard swap keeps the
    // mini-galaxy responsive.
    currentUi.focusedSystemId = hit;
    currentUi.selection = { kind: 'system', id: hit };
    // Reset the system camera so the new system opens framed.
    currentUi.systemCamera = defaultSystemCamera();
    e.stopPropagation();
  }
  canvas.addEventListener('click', onClick);

  return {
    update(state: GameState, ui: UiState): void {
      currentState = state;
      currentUi = ui;

      if (ui.viewMode !== 'system') {
        root.style.display = 'none';
        return;
      }
      root.style.display = 'block';

      const size = MINI_GALAXY_SIZE_PX;
      // Clear.
      ctx.clearRect(0, 0, size, size);

      // Subtle grid so the box reads as a map, not empty space.
      ctx.strokeStyle = 'rgba(60, 80, 120, 0.25)';
      ctx.lineWidth = 1;
      for (let i = 1; i < 4; i++) {
        const p = (i * size) / 4;
        ctx.beginPath();
        ctx.moveTo(p, 0);
        ctx.lineTo(p, size);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(0, p);
        ctx.lineTo(size, p);
        ctx.stroke();
      }

      // Systems.
      for (const sys of state.systems.values()) {
        const sx = sys.x * size;
        const sy = sys.y * size;
        const isFocused = sys.id === ui.focusedSystemId;

        if (isFocused) {
          ctx.strokeStyle = '#8fffb0';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(sx, sy, FOCUSED_RING_RADIUS, 0, Math.PI * 2);
          ctx.stroke();
        }

        ctx.fillStyle = isFocused ? '#ffe8a0' : '#ffd27a';
        ctx.beginPath();
        ctx.arc(sx, sy, DOT_RADIUS, 0, Math.PI * 2);
        ctx.fill();
      }

      // Bottom-left label so the player knows what this overlay is.
      ctx.fillStyle = '#7a8bb5';
      ctx.font = '9px inherit';
      ctx.textAlign = 'left';
      ctx.fillText('GALAXY', 6, size - 6);
    },
    destroy(): void {
      canvas.removeEventListener('click', onClick);
      root.remove();
    },
  };
}
